import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import useOrgConfig from 'src/hooks/discover/useOrgConfig';
import { useIsInAssist } from 'src/hooks/useIsInAssist';
import {
  selectIsUserSuperAdmin,
  selectIsUserSuperAdminOrAdmin,
  selectUserRole,
} from 'src/reducers/userReducer/userReducer';
import { UserRoles } from 'src/utils/enums';

/**
 * Whether the current user can see and author Human Evals conversation
 * reviews (review-status column, flagged filter, human feedback card,
 * verdict card).
 *
 * `human_evals` is the customer-facing org rollout flag. Reviews are
 * admin-only unless the org also enables `human_evals_user_access`, which
 * extends access to USER roles — mirroring the BE conversation-review gate.
 * `human_evals_internal` gives super admins QA access before the customer
 * flag is flipped.
 */
export const useIsHumanEvalsEnabled = (): boolean => {
  const { feature_flags: dashboardFeatureFlags = [] } = useOrgConfig();
  const isSuperAdmin = useSelector(selectIsUserSuperAdmin);
  const isAdminOrSuperAdmin = useSelector(selectIsUserSuperAdminOrAdmin);
  const userRole = useSelector(selectUserRole);

  // ROLE_USER only — the BE extends review access to that role alone, so
  // ROLE_AGENT stays blocked even in an org that enables user access.
  const hasReviewRoleAccess =
    isAdminOrSuperAdmin ||
    (userRole === UserRoles.ROLE_USER &&
      dashboardFeatureFlags.includes('human_evals_user_access'));

  return (
    (dashboardFeatureFlags.includes('human_evals') && hasReviewRoleAccess) ||
    (dashboardFeatureFlags.includes('human_evals_internal') && isSuperAdmin)
  );
};

/**
 * Whether the current user can see Improved Policy surfaces (agent-insights
 * sub-tab, inline review, workflow badge and filter). Improved Policy edits
 * workflows, so it stays admin-only regardless of `human_evals_user_access` —
 * mirroring the BE policy-diff gate.
 *
 * Improved Policy is only surfaced on Widget. Tables use `channel` and the
 * editor uses `view`. Solve defaults to Widget; the Assist table defaults to
 * Playbook when no channel is specified, matching useSolveMetricsQueryParams.
 * This is a UI restriction, not channel-level recommendation provenance.
 */
export const useIsImprovedPolicyEnabled = (): boolean => {
  const isInAssist = useIsInAssist();
  const [params] = useSearchParams();
  const defaultChannel = isInAssist ? 'playbook' : 'widget';
  const channel = params.get('view') || params.get('channel') || defaultChannel;
  const { feature_flags: dashboardFeatureFlags = [] } = useOrgConfig();
  const isSuperAdmin = useSelector(selectIsUserSuperAdmin);
  const isAdminOrSuperAdmin = useSelector(selectIsUserSuperAdminOrAdmin);

  return (
    channel === 'widget' &&
    ((dashboardFeatureFlags.includes('human_evals') && isAdminOrSuperAdmin) ||
      (dashboardFeatureFlags.includes('human_evals_internal') && isSuperAdmin))
  );
};
